import { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import type { Polygon, Position } from "geojson";

import {
  useAddIgnitionRegime,
  useAddIgnitionsBulkToRegime,
  useAddIgnitionToRegime,
} from "../api/ignitionRegimes";
import {
  DEFAULT_GRID_SPACING_M,
  DEFAULT_UNCERTAINTY_RADIUS_M,
  MIN_GRID_SPACING_M,
  uncertaintyGrid,
} from "../utils/uncertaintyGrid";
import { IgnitionRegimeContext } from "../contexts/IgnitionRegimeContext";

export type PendingIgnition =
  | { kind: "point"; coordinates: Position; label: string }
  | { kind: "cluster"; hull: Polygon; detectionCount: number; label: string };

interface CreateIgnitionFromDetectionsDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
  pending: PendingIgnition | null;
}

type RegimeTarget = "current" | "new";
type PointMode = "single" | "grid";

const defaultRegimeName = () =>
  `Detections ${new Date().toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  })}`;

const CreateIgnitionFromDetectionsDialog: React.FC<
  CreateIgnitionFromDetectionsDialogProps
> = ({ open, onClose, projectId, pending }) => {
  const { selectedRegime, setSelectedRegime } = useContext(IgnitionRegimeContext);
  const addRegime = useAddIgnitionRegime(projectId);
  const addIgnition = useAddIgnitionToRegime(projectId);
  const addIgnitionsBulk = useAddIgnitionsBulkToRegime(projectId);

  const [target, setTarget] = useState<RegimeTarget>("current");
  const [regimeName, setRegimeName] = useState(defaultRegimeName());
  const [ignitionName, setIgnitionName] = useState("");
  const [pointMode, setPointMode] = useState<PointMode>("single");
  // Held as text while editing. Clamping on every keystroke makes the field
  // impossible to type in: clearing it to type a new number yields "", which
  // parses to 0 and snaps straight to the minimum.
  const [uncertaintyRadiusText, setUncertaintyRadiusText] = useState(
    String(DEFAULT_UNCERTAINTY_RADIUS_M)
  );
  const [gridSpacingText, setGridSpacingText] = useState(
    String(DEFAULT_GRID_SPACING_M)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // With no regime selected there is nothing to add to, so default to
  // creating one.
  useEffect(() => {
    if (!open) return;
    setTarget(selectedRegime ? "current" : "new");
    setRegimeName(defaultRegimeName());
    setIgnitionName(pending?.label ?? "");
    setPointMode("single");
    setUncertaintyRadiusText(String(DEFAULT_UNCERTAINTY_RADIUS_M));
    setGridSpacingText(String(DEFAULT_GRID_SPACING_M));
    setError(null);
  }, [open, selectedRegime, pending]);

  const uncertaintyRadius = Math.max(0, Number(uncertaintyRadiusText) || 0);
  const gridSpacing = Math.max(
    MIN_GRID_SPACING_M,
    Number(gridSpacingText) || DEFAULT_GRID_SPACING_M
  );

  if (!pending) return null;

  const handleCreate = async () => {
    setIsSaving(true);
    setError(null);
    try {
      let regimeId = selectedRegime?.id;

      if (target === "new") {
        const created = await addRegime.mutateAsync({
          name: regimeName.trim() || defaultRegimeName(),
          mode: "blank",
        });
        // This endpoint returns the regime bare, not inside the usual
        // {data: ...} envelope, so accept either shape rather than relying on
        // the list refetch happening to put the newest regime first.
        const regime = ((created as any)?.data ?? created) as { id: number };
        regimeId = regime?.id;
        // Switch the map to the new regime so the ignition is visible
        // immediately (the dropdown re-syncs from the refetched list).
        setSelectedRegime(created.data);
      }

      if (!regimeId) {
        setError("No ignition regime selected.");
        setIsSaving(false);
        return;
      }

      const baseName = ignitionName.trim() || pending.label;

      if (pending.kind === "point" && pointMode === "grid") {
        const grid = uncertaintyGrid(
          pending.coordinates as [number, number],
          uncertaintyRadius,
          gridSpacing
        );
        await addIgnitionsBulk.mutateAsync({
          regimeId,
          ignitions: grid.map((coordinates, index) => ({
            geometry: { type: "Point" as const, coordinates },
            name: `${baseName} (${index + 1}/${grid.length})`.slice(0, 255),
          })),
        });
      } else {
        const geometry =
          pending.kind === "point"
            ? { type: "Point" as const, coordinates: pending.coordinates }
            : pending.hull;
        await addIgnition.mutateAsync({ regimeId, geometry, name: baseName });
      }
      onClose();
    } catch (e: any) {
      setError(e?.message ?? "Failed to create the ignition.");
    } finally {
      setIsSaving(false);
    }
  };

  const gridPoints =
    pending.kind === "point" && pointMode === "grid"
      ? uncertaintyGrid(
          pending.coordinates as [number, number],
          uncertaintyRadius,
          gridSpacing
        ).length
      : 0;

  const summary =
    pending.kind === "point"
      ? pointMode === "grid"
        ? `${gridPoints} point ignitions will be created across this detection's area of uncertainty — the simulator runs each as an independent fire.`
        : "A point ignition will be created at this detection."
      : pending.detectionCount > 0
      ? `A polygon ignition will be created over ${pending.detectionCount} detections.`
      : // A mapped burnt-area perimeter, not a hull through detection points.
        "A polygon ignition will be created over this mapped area.";

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {pending.kind === "point"
          ? "Create ignition from detection"
          : pending.detectionCount > 0
          ? "Create ignition from cluster"
          : "Create ignition from area"}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {summary}
        </Typography>

        <TextField
          label="Ignition name"
          value={ignitionName}
          onChange={(e) => setIgnitionName(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        />

        {pending.kind === "point" && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Ignition shape
            </Typography>
            <RadioGroup
              value={pointMode}
              onChange={(e) => setPointMode(e.target.value as PointMode)}
            >
              <FormControlLabel
                value="single"
                control={<Radio size="small" />}
                label="A single point at the detection"
              />
              <FormControlLabel
                value="grid"
                control={<Radio size="small" />}
                label="A grid across its area of uncertainty"
              />
            </RadioGroup>

            {pointMode === "grid" && (
              <Box sx={{ display: "flex", gap: 1, mt: 1, mb: 2 }}>
                <TextField
                  label="Uncertainty radius (m)"
                  type="number"
                  size="small"
                  value={uncertaintyRadiusText}
                  onChange={(e) => setUncertaintyRadiusText(e.target.value)}
                  onBlur={() => setUncertaintyRadiusText(String(uncertaintyRadius))}
                  inputProps={{ min: 0, step: 10 }}
                  fullWidth
                  helperText="Default is half a VIIRS pixel"
                />
                <TextField
                  label="Spacing (m)"
                  type="number"
                  size="small"
                  value={gridSpacingText}
                  onChange={(e) => setGridSpacingText(e.target.value)}
                  onBlur={() => setGridSpacingText(String(gridSpacing))}
                  inputProps={{ min: MIN_GRID_SPACING_M, step: 25 }}
                  fullWidth
                  helperText={`${gridPoints} ignitions · min ${MIN_GRID_SPACING_M} m`}
                />
              </Box>
            )}
          </>
        )}

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Add to
        </Typography>
        <RadioGroup
          value={target}
          onChange={(e) => setTarget(e.target.value as RegimeTarget)}
        >
          <FormControlLabel
            value="current"
            control={<Radio size="small" />}
            disabled={!selectedRegime}
            label={
              selectedRegime
                ? `Current regime — ${selectedRegime.name}`
                : "Current regime (none selected)"
            }
          />
          <FormControlLabel
            value="new"
            control={<Radio size="small" />}
            label="A new blank regime"
          />
        </RadioGroup>

        {target === "new" && (
          <TextField
            label="New regime name"
            value={regimeName}
            onChange={(e) => setRegimeName(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
          />
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={isSaving}>
          {isSaving ? <CircularProgress size={20} /> : "Create ignition"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateIgnitionFromDetectionsDialog;
